import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useProjectsContext } from '@/providers/projects-provider';
import { FILTERS } from '@/models/common/filters';
import { QUERY_KEY_CONVERSATION_DETAILS, QUERY_KEY_INTERACTION_DETAILS, QUERY_KEY_AUDIENCES_INTERACTIONS, QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { resolveAppliedLabels, ensureLabelsColumnSelected } from './labels.util';
import { getDefaultInteractionColumns, getDefaultConversationColumns } from './use-interaction-columns-state';
import { getAvailableConversationLabels, useAddConversationLabel, useDeleteConversationLabel } from '@/api/generated/conversations/conversations';
import { getAvailableInteractionLabels, useAddInteractionLabel, useDeleteInteractionLabel } from '@/api/generated/interactions/interactions';
export { resolveAppliedLabels };
function addLabelToAvailableCaches(queryClient: ReturnType<typeof useQueryClient>, projectId: string | null | undefined, scope: 'interaction' | 'conversation', labelName: string) {
  const queryKeyPrefix = scope === 'conversation' ? 'getAvailableConversationLabels' : 'getAvailableInteractionLabels';
  const filterKind = scope === 'conversation' ? FILTERS.kindConversationLabel : FILTERS.kindInteractionLabel;
  const normalized = labelName.trim();
  if (!normalized) return;
  queryClient.setQueryData([queryKeyPrefix, projectId], (old: unknown) => {
    if (!old) return old;
    const current = (old as {
      labels?: string[];
    })?.labels ?? [];
    const labels = Array.from(new Set([...current, normalized]));
    return {
      ...(old as Record<string, unknown>),
      labels
    };
  });
  queryClient.setQueryData(['availableLabelsForFilter', projectId, filterKind], (old: unknown) => {
    if (!old) return old;
    const current = (old as {
      labels?: string[];
    })?.labels ?? [];
    const labels = Array.from(new Set([...current, normalized]));
    return {
      ...(old as Record<string, unknown>),
      labels
    };
  });
}
function invalidateAvailableLabelCaches(queryClient: ReturnType<typeof useQueryClient>, projectId: string | null | undefined, scope: 'interaction' | 'conversation') {
  const queryKeyPrefix = scope === 'conversation' ? 'getAvailableConversationLabels' : 'getAvailableInteractionLabels';
  const filterKind = scope === 'conversation' ? FILTERS.kindConversationLabel : FILTERS.kindInteractionLabel;
  queryClient.invalidateQueries({
    queryKey: [queryKeyPrefix, projectId]
  });
  queryClient.invalidateQueries({
    queryKey: ['availableLabelsForFilter', projectId, filterKind]
  });
}
export function useAvailableLabelNames(projectId) {
  return useAvailableLabelNamesByScope(projectId, "interaction");
}
export function useAvailableLabelNamesByScope(projectId, scope) {
  const $ = _c(15);
  const queryKeyPrefix = scope === "conversation" ? "getAvailableConversationLabels" : "getAvailableInteractionLabels";
  const fetchLabels = scope === "conversation" ? getAvailableConversationLabels : getAvailableInteractionLabels;
  let t0;
  if ($[0] !== projectId || $[1] !== queryKeyPrefix) {
    t0 = [queryKeyPrefix, projectId];
    $[0] = projectId;
    $[1] = queryKeyPrefix;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== fetchLabels || $[4] !== projectId) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return fetchLabels({
        project_id: projectId
      }, {
        signal
      });
    };
    $[3] = fetchLabels;
    $[4] = projectId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!projectId;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data,
    isLoading
  } = useQuery(t3);
  let t4;
  if ($[10] !== data?.labels) {
    t4 = Array.from(new Set((data?.labels ?? []).map(_temp).filter(Boolean)));
    $[10] = data?.labels;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const availableLabels = t4;
  let t5;
  if ($[12] !== availableLabels || $[13] !== isLoading) {
    t5 = {
      availableLabels,
      isLoading
    };
    $[12] = availableLabels;
    $[13] = isLoading;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp(label) {
  return label.trim();
}
export function useInteractionLabels(interactionId, appliedFilters) {
  const $ = _c(31);
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== appliedFilters) {
    t0 = appliedFilters?.some(_temp2) ?? false;
    $[0] = appliedFilters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasInteractionLabelFilter = t0;
  let t1;
  if ($[2] !== interactionId || $[3] !== queryClient) {
    t1 = (labelName, adding) => {
      const detailSnapshots = queryClient.getQueriesData({
        queryKey: [QUERY_KEY_INTERACTION_DETAILS]
      }).filter(t2 => {
        const [queryKey] = t2;
        const payload = Array.isArray(queryKey) ? queryKey[1] as {
          interaction_id?: string;
        } | undefined : undefined;
        return payload?.interaction_id === interactionId;
      });
      const listSnapshots = queryClient.getQueriesData({
        queryKey: ["interactions"]
      });
      const audiencesSnapshots = queryClient.getQueriesData({
        queryKey: [QUERY_KEY_AUDIENCES_INTERACTIONS]
      });
      detailSnapshots.forEach(t3 => {
        const [queryKey_0] = t3;
        queryClient.setQueryData(queryKey_0, old => {
          if (!old || typeof old !== "object") {
            return old;
          }
          const oldData = old as {
            details?: {
              labels?: string[];
            };
          };
          const details = oldData.details;
          if (!details) {
            return old;
          }
          const current = Array.isArray(details.labels) ? details.labels : [];
          const next = adding ? Array.from(new Set([...current, labelName])) : current.filter(label => label !== labelName);
          return {
            ...oldData,
            details: {
              ...details,
              labels: next
            }
          };
        });
      });
      listSnapshots.forEach(t4 => {
        const [queryKey_1] = t4;
        queryClient.setQueryData(queryKey_1, old_0 => {
          if (!old_0 || typeof old_0 !== "object") {
            return old_0;
          }
          const oldData_0 = old_0 as {
            data?: Array<{
              id: string;
              labels?: string[];
            }>;
          };
          if (!Array.isArray(oldData_0.data)) {
            return old_0;
          }
          const nextData = oldData_0.data.map(item => {
            if (item.id !== interactionId) {
              return item;
            }
            const current_0 = Array.isArray(item.labels) ? item.labels : [];
            const next_0 = adding ? Array.from(new Set([...current_0, labelName])) : current_0.filter(label_0 => label_0 !== labelName);
            return {
              ...item,
              labels: next_0
            };
          });
          return {
            ...oldData_0,
            data: nextData
          };
        });
      });
      audiencesSnapshots.forEach(t5 => {
        const [queryKey_2] = t5;
        queryClient.setQueryData(queryKey_2, old_1 => {
          if (!old_1 || typeof old_1 !== "object") {
            return old_1;
          }
          const oldData_1 = old_1 as {
            data?: Array<{
              id: string;
              labels?: string[];
            }>;
          };
          if (!Array.isArray(oldData_1.data)) {
            return old_1;
          }
          const nextData_0 = oldData_1.data.map(item_0 => {
            if (item_0.id !== interactionId) {
              return item_0;
            }
            const current_1 = Array.isArray(item_0.labels) ? item_0.labels : [];
            const next_1 = adding ? Array.from(new Set([...current_1, labelName])) : current_1.filter(label_1 => label_1 !== labelName);
            return {
              ...item_0,
              labels: next_1
            };
          });
          return {
            ...oldData_1,
            data: nextData_0
          };
        });
      });
      return () => {
        detailSnapshots.forEach(t6 => {
          const [queryKey_3, data] = t6;
          queryClient.setQueryData(queryKey_3, data);
        });
        listSnapshots.forEach(t7 => {
          const [queryKey_4, data_0] = t7;
          queryClient.setQueryData(queryKey_4, data_0);
        });
        audiencesSnapshots.forEach(t8 => {
          const [queryKey_5, data_1] = t8;
          queryClient.setQueryData(queryKey_5, data_1);
        });
      };
    };
    $[2] = interactionId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const optimisticUpdate = t1;
  let t2;
  if ($[5] !== hasInteractionLabelFilter || $[6] !== projectId || $[7] !== queryClient) {
    t2 = () => {
      invalidateAvailableLabelCaches(queryClient, projectId, "interaction");
      if (hasInteractionLabelFilter) {
        queryClient.invalidateQueries({
          queryKey: ["interactions"]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_AUDIENCES_INTERACTIONS]
        });
      }
    };
    $[5] = hasInteractionLabelFilter;
    $[6] = projectId;
    $[7] = queryClient;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const invalidate = t2;
  let t3;
  if ($[9] !== invalidate) {
    t3 = {
      mutation: {
        onSuccess: invalidate
      }
    };
    $[9] = invalidate;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    mutateAsync: addLabelMutation,
    isPending: isAdding
  } = useAddInteractionLabel(t3);
  let t4;
  if ($[11] !== invalidate) {
    t4 = {
      mutation: {
        onSuccess: invalidate
      }
    };
    $[11] = invalidate;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    mutateAsync: deleteLabelMutation
  } = useDeleteInteractionLabel(t4);
  let t5;
  if ($[13] !== addLabelMutation || $[14] !== interactionId || $[15] !== optimisticUpdate || $[16] !== projectId || $[17] !== queryClient) {
    t5 = labelName_0 => {
      if (!projectId || !interactionId) {
        return;
      }
      ensureLabelsColumnSelected("interaction", getDefaultInteractionColumns());
      addLabelToAvailableCaches(queryClient, projectId, "interaction", labelName_0);
      const rollback = optimisticUpdate(labelName_0, true);
      addLabelMutation({
        data: {
          project_id: projectId,
          interaction_id: interactionId,
          name: labelName_0
        }
      }).catch(() => {
        rollback();
      });
    };
    $[13] = addLabelMutation;
    $[14] = interactionId;
    $[15] = optimisticUpdate;
    $[16] = projectId;
    $[17] = queryClient;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const addLabel = t5;
  let t6;
  if ($[19] !== deleteLabelMutation || $[20] !== interactionId || $[21] !== optimisticUpdate || $[22] !== projectId) {
    t6 = labelName_1 => {
      if (!projectId || !interactionId) {
        return;
      }
      const rollback_0 = optimisticUpdate(labelName_1, false);
      deleteLabelMutation({
        data: {
          project_id: projectId,
          interaction_id: interactionId,
          name: labelName_1
        }
      }).catch(() => {
        rollback_0();
      });
    };
    $[19] = deleteLabelMutation;
    $[20] = interactionId;
    $[21] = optimisticUpdate;
    $[22] = projectId;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const removeLabel = t6;
  let t7;
  if ($[24] !== addLabel) {
    t7 = labelName_2 => addLabel(labelName_2);
    $[24] = addLabel;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const createAndAddLabel = t7;
  let t8;
  if ($[26] !== addLabel || $[27] !== createAndAddLabel || $[28] !== isAdding || $[29] !== removeLabel) {
    t8 = {
      addLabel,
      removeLabel,
      createAndAddLabel,
      isAdding
    };
    $[26] = addLabel;
    $[27] = createAndAddLabel;
    $[28] = isAdding;
    $[29] = removeLabel;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function _temp2(f) {
  return f.column === FILTERS.columnInteractionLabel;
}
export function useConversationLabels(conversationId, appliedFilters) {
  const $ = _c(31);
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== appliedFilters) {
    t0 = appliedFilters?.some(_temp3) ?? false;
    $[0] = appliedFilters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasConversationLabelFilter = t0;
  let t1;
  if ($[2] !== conversationId || $[3] !== queryClient) {
    t1 = (labelName, adding) => {
      const detailSnapshots = queryClient.getQueriesData({
        queryKey: [QUERY_KEY_CONVERSATION_DETAILS]
      }).filter(t2 => {
        const [queryKey] = t2;
        const payload = Array.isArray(queryKey) ? queryKey[1] as {
          conversation_id?: string;
        } | undefined : undefined;
        return payload?.conversation_id === conversationId;
      });
      const aggregatesSnapshots = queryClient.getQueriesData({
        queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
      });
      detailSnapshots.forEach(t3 => {
        const [queryKey_0] = t3;
        queryClient.setQueryData(queryKey_0, old => {
          if (!old || typeof old !== "object") {
            return old;
          }
          const oldData = old as {
            labels?: string[];
          };
          const current = Array.isArray(oldData.labels) ? oldData.labels : [];
          const next = adding ? Array.from(new Set([...current, labelName])) : current.filter(label => label !== labelName);
          return {
            ...oldData,
            labels: next
          };
        });
      });
      aggregatesSnapshots.forEach(t4 => {
        const [queryKey_1] = t4;
        queryClient.setQueryData(queryKey_1, old_0 => {
          if (!old_0 || typeof old_0 !== "object") {
            return old_0;
          }
          const updateLabels = current_0 => adding ? Array.from(new Set([...(Array.isArray(current_0) ? current_0 : []), labelName])) : (Array.isArray(current_0) ? current_0 : []).filter(label_0 => label_0 !== labelName);
          const oldData_0 = old_0 as {
            data?: Array<{
              group_name?: string | null;
              conversation_id?: string | null;
              conversation_labels?: (string | null)[];
            }>;
            aggregates?: Array<{
              groupName?: string | null;
              conversationId?: string | null;
              conversationLabels?: (string | null)[];
            }>;
          };
          const nextRawData = Array.isArray(oldData_0.data) ? oldData_0.data.map(item => {
            const rowConversationId = item.conversation_id ?? item.group_name;
            if (rowConversationId !== conversationId) {
              return item;
            }
            return {
              ...item,
              conversation_labels: updateLabels(item.conversation_labels)
            };
          }) : oldData_0.data;
          const nextMappedAggregates = Array.isArray(oldData_0.aggregates) ? oldData_0.aggregates.map(item_0 => {
            const rowConversationId_0 = item_0.conversationId ?? item_0.groupName;
            if (rowConversationId_0 !== conversationId) {
              return item_0;
            }
            return {
              ...item_0,
              conversationLabels: updateLabels(item_0.conversationLabels)
            };
          }) : oldData_0.aggregates;
          return {
            ...oldData_0,
            data: nextRawData,
            aggregates: nextMappedAggregates
          };
        });
      });
      return () => {
        detailSnapshots.forEach(t5 => {
          const [queryKey_2, data] = t5;
          queryClient.setQueryData(queryKey_2, data);
        });
        aggregatesSnapshots.forEach(t6 => {
          const [queryKey_3, data_0] = t6;
          queryClient.setQueryData(queryKey_3, data_0);
        });
      };
    };
    $[2] = conversationId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const optimisticUpdate = t1;
  let t2;
  if ($[5] !== hasConversationLabelFilter || $[6] !== projectId || $[7] !== queryClient) {
    t2 = () => {
      invalidateAvailableLabelCaches(queryClient, projectId, "conversation");
      if (hasConversationLabelFilter) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
        });
      }
    };
    $[5] = hasConversationLabelFilter;
    $[6] = projectId;
    $[7] = queryClient;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const invalidate = t2;
  let t3;
  if ($[9] !== invalidate) {
    t3 = {
      mutation: {
        onSuccess: invalidate
      }
    };
    $[9] = invalidate;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    mutateAsync: addLabelMutation,
    isPending: isAdding
  } = useAddConversationLabel(t3);
  let t4;
  if ($[11] !== invalidate) {
    t4 = {
      mutation: {
        onSuccess: invalidate
      }
    };
    $[11] = invalidate;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    mutateAsync: deleteLabelMutation
  } = useDeleteConversationLabel(t4);
  let t5;
  if ($[13] !== addLabelMutation || $[14] !== conversationId || $[15] !== optimisticUpdate || $[16] !== projectId || $[17] !== queryClient) {
    t5 = labelName_0 => {
      if (!projectId || !conversationId) {
        return;
      }
      ensureLabelsColumnSelected("conversation", getDefaultConversationColumns());
      addLabelToAvailableCaches(queryClient, projectId, "conversation", labelName_0);
      const rollback = optimisticUpdate(labelName_0, true);
      addLabelMutation({
        data: {
          project_id: projectId,
          conversation_id: conversationId,
          name: labelName_0
        }
      }).catch(() => {
        rollback();
      });
    };
    $[13] = addLabelMutation;
    $[14] = conversationId;
    $[15] = optimisticUpdate;
    $[16] = projectId;
    $[17] = queryClient;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const addLabel = t5;
  let t6;
  if ($[19] !== conversationId || $[20] !== deleteLabelMutation || $[21] !== optimisticUpdate || $[22] !== projectId) {
    t6 = labelName_1 => {
      if (!projectId || !conversationId) {
        return;
      }
      const rollback_0 = optimisticUpdate(labelName_1, false);
      deleteLabelMutation({
        data: {
          project_id: projectId,
          conversation_id: conversationId,
          name: labelName_1
        }
      }).catch(() => {
        rollback_0();
      });
    };
    $[19] = conversationId;
    $[20] = deleteLabelMutation;
    $[21] = optimisticUpdate;
    $[22] = projectId;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const removeLabel = t6;
  let t7;
  if ($[24] !== addLabel) {
    t7 = labelName_2 => addLabel(labelName_2);
    $[24] = addLabel;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const createAndAddLabel = t7;
  let t8;
  if ($[26] !== addLabel || $[27] !== createAndAddLabel || $[28] !== isAdding || $[29] !== removeLabel) {
    t8 = {
      addLabel,
      removeLabel,
      createAndAddLabel,
      isAdding
    };
    $[26] = addLabel;
    $[27] = createAndAddLabel;
    $[28] = isAdding;
    $[29] = removeLabel;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function _temp3(f) {
  return f.column === FILTERS.columnConversationLabel;
}
export function useBulkInteractionLabels(interactionIds, appliedFilters) {
  const $ = _c(22);
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== appliedFilters) {
    t0 = appliedFilters?.some(_temp4) ?? false;
    $[0] = appliedFilters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasInteractionLabelFilter = t0;
  const {
    mutateAsync: addLabelMutation,
    isPending: isAdding
  } = useAddInteractionLabel();
  const {
    mutateAsync: deleteLabelMutation,
    isPending: isRemoving
  } = useDeleteInteractionLabel();
  let t1;
  if ($[2] !== queryClient) {
    t1 = (uniqueIds, labelName, adding) => {
      const patchList = old => {
        if (!old || typeof old !== "object") {
          return old;
        }
        const oldData = old as {
          data?: Array<{
            id: string;
            labels?: string[];
          }>;
        };
        if (!Array.isArray(oldData.data)) {
          return old;
        }
        return {
          ...oldData,
          data: oldData.data.map(item => {
            if (!uniqueIds.includes(item.id)) {
              return item;
            }
            const current = Array.isArray(item.labels) ? item.labels : [];
            const next = adding ? Array.from(new Set([...current, labelName])) : current.filter(l => l !== labelName);
            return {
              ...item,
              labels: next
            };
          })
        };
      };
      queryClient.setQueriesData({
        queryKey: ["interactions"]
      }, patchList);
      queryClient.setQueriesData({
        queryKey: [QUERY_KEY_AUDIENCES_INTERACTIONS]
      }, patchList);
      queryClient.getQueriesData({
        queryKey: [QUERY_KEY_INTERACTION_DETAILS]
      }).filter(t2 => {
        const [queryKey] = t2;
        const payload = Array.isArray(queryKey) ? queryKey[1] as {
          interaction_id?: string;
        } | undefined : undefined;
        return !!payload?.interaction_id && uniqueIds.includes(payload.interaction_id);
      }).forEach(t3 => {
        const [queryKey_0] = t3;
        queryClient.setQueryData(queryKey_0, old_0 => {
          if (!old_0 || typeof old_0 !== "object") {
            return old_0;
          }
          const oldData_0 = old_0 as {
            details?: {
              labels?: string[];
            };
          };
          if (!oldData_0.details) {
            return old_0;
          }
          const current_0 = Array.isArray(oldData_0.details.labels) ? oldData_0.details.labels : [];
          const next_0 = adding ? Array.from(new Set([...current_0, labelName])) : current_0.filter(l_0 => l_0 !== labelName);
          return {
            ...oldData_0,
            details: {
              ...oldData_0.details,
              labels: next_0
            }
          };
        });
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const patchInteractionsCache = t1;
  let t2;
  if ($[4] !== addLabelMutation || $[5] !== hasInteractionLabelFilter || $[6] !== interactionIds || $[7] !== patchInteractionsCache || $[8] !== projectId || $[9] !== queryClient) {
    t2 = async labelName_0 => {
      if (!projectId || interactionIds.length === 0) {
        return;
      }
      const uniqueIds_0 = Array.from(new Set(interactionIds));
      ensureLabelsColumnSelected("interaction", getDefaultInteractionColumns());
      patchInteractionsCache(uniqueIds_0, labelName_0, true);
      addLabelToAvailableCaches(queryClient, projectId, "interaction", labelName_0);
      await Promise.allSettled(uniqueIds_0.map(interactionId => addLabelMutation({
        data: {
          project_id: projectId,
          interaction_id: interactionId,
          name: labelName_0
        }
      })));
      invalidateAvailableLabelCaches(queryClient, projectId, "interaction");
      if (hasInteractionLabelFilter) {
        queryClient.invalidateQueries({
          queryKey: ["interactions"]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_AUDIENCES_INTERACTIONS]
        });
      }
    };
    $[4] = addLabelMutation;
    $[5] = hasInteractionLabelFilter;
    $[6] = interactionIds;
    $[7] = patchInteractionsCache;
    $[8] = projectId;
    $[9] = queryClient;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const addLabelToMany = t2;
  let t3;
  if ($[11] !== deleteLabelMutation || $[12] !== hasInteractionLabelFilter || $[13] !== interactionIds || $[14] !== patchInteractionsCache || $[15] !== projectId || $[16] !== queryClient) {
    t3 = async labelName_1 => {
      if (!projectId || interactionIds.length === 0) {
        return;
      }
      const uniqueIds_1 = Array.from(new Set(interactionIds));
      patchInteractionsCache(uniqueIds_1, labelName_1, false);
      await Promise.allSettled(uniqueIds_1.map(interactionId_0 => deleteLabelMutation({
        data: {
          project_id: projectId,
          interaction_id: interactionId_0,
          name: labelName_1
        }
      })));
      invalidateAvailableLabelCaches(queryClient, projectId, "interaction");
      if (hasInteractionLabelFilter) {
        queryClient.invalidateQueries({
          queryKey: ["interactions"]
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_AUDIENCES_INTERACTIONS]
        });
      }
    };
    $[11] = deleteLabelMutation;
    $[12] = hasInteractionLabelFilter;
    $[13] = interactionIds;
    $[14] = patchInteractionsCache;
    $[15] = projectId;
    $[16] = queryClient;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const removeLabelFromMany = t3;
  const t4 = isAdding || isRemoving;
  let t5;
  if ($[18] !== addLabelToMany || $[19] !== removeLabelFromMany || $[20] !== t4) {
    t5 = {
      addLabelToMany,
      removeLabelFromMany,
      isAdding: t4
    };
    $[18] = addLabelToMany;
    $[19] = removeLabelFromMany;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}
function _temp4(f) {
  return f.column === FILTERS.columnInteractionLabel;
}
export function useBulkConversationLabels(conversationIds, appliedFilters) {
  const $ = _c(22);
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== appliedFilters) {
    t0 = appliedFilters?.some(_temp5) ?? false;
    $[0] = appliedFilters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasConversationLabelFilter = t0;
  const {
    mutateAsync: addLabelMutation,
    isPending: isAdding
  } = useAddConversationLabel();
  const {
    mutateAsync: deleteLabelMutation,
    isPending: isRemoving
  } = useDeleteConversationLabel();
  let t1;
  if ($[2] !== queryClient) {
    t1 = (uniqueIds, labelName, adding) => {
      queryClient.setQueriesData({
        queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
      }, old => {
        if (!old || typeof old !== "object") {
          return old;
        }
        const oldData = old as {
          data?: Array<{
            conversation_id?: string | null;
            group_name?: string | null;
            conversation_labels?: (string | null)[];
          }>;
        };
        if (!Array.isArray(oldData.data)) {
          return old;
        }
        return {
          ...oldData,
          data: oldData.data.map(item => {
            const rowId = item.conversation_id ?? item.group_name;
            if (!rowId || !uniqueIds.includes(rowId)) {
              return item;
            }
            const current = (item.conversation_labels ?? []).filter(_temp6);
            const next = adding ? Array.from(new Set([...current, labelName])) : current.filter(l_0 => l_0 !== labelName);
            return {
              ...item,
              conversation_labels: next
            };
          })
        };
      });
      queryClient.getQueriesData({
        queryKey: [QUERY_KEY_CONVERSATION_DETAILS]
      }).filter(t2 => {
        const [queryKey] = t2;
        const payload = Array.isArray(queryKey) ? queryKey[1] as {
          conversation_id?: string;
        } | undefined : undefined;
        return !!payload?.conversation_id && uniqueIds.includes(payload.conversation_id);
      }).forEach(t3 => {
        const [queryKey_0] = t3;
        queryClient.setQueryData(queryKey_0, old_0 => {
          if (!old_0 || typeof old_0 !== "object") {
            return old_0;
          }
          const oldData_0 = old_0 as {
            labels?: string[];
          };
          const current_0 = Array.isArray(oldData_0.labels) ? oldData_0.labels : [];
          const next_0 = adding ? Array.from(new Set([...current_0, labelName])) : current_0.filter(l_1 => l_1 !== labelName);
          return {
            ...oldData_0,
            labels: next_0
          };
        });
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const patchConversationsCache = t1;
  let t2;
  if ($[4] !== addLabelMutation || $[5] !== conversationIds || $[6] !== hasConversationLabelFilter || $[7] !== patchConversationsCache || $[8] !== projectId || $[9] !== queryClient) {
    t2 = async labelName_0 => {
      if (!projectId || conversationIds.length === 0) {
        return;
      }
      const uniqueIds_0 = Array.from(new Set(conversationIds));
      ensureLabelsColumnSelected("conversation", getDefaultConversationColumns());
      patchConversationsCache(uniqueIds_0, labelName_0, true);
      addLabelToAvailableCaches(queryClient, projectId, "conversation", labelName_0);
      await Promise.allSettled(uniqueIds_0.map(conversationId => addLabelMutation({
        data: {
          project_id: projectId,
          conversation_id: conversationId,
          name: labelName_0
        }
      })));
      invalidateAvailableLabelCaches(queryClient, projectId, "conversation");
      if (hasConversationLabelFilter) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
        });
      }
    };
    $[4] = addLabelMutation;
    $[5] = conversationIds;
    $[6] = hasConversationLabelFilter;
    $[7] = patchConversationsCache;
    $[8] = projectId;
    $[9] = queryClient;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const addLabelToMany = t2;
  let t3;
  if ($[11] !== conversationIds || $[12] !== deleteLabelMutation || $[13] !== hasConversationLabelFilter || $[14] !== patchConversationsCache || $[15] !== projectId || $[16] !== queryClient) {
    t3 = async labelName_1 => {
      if (!projectId || conversationIds.length === 0) {
        return;
      }
      const uniqueIds_1 = Array.from(new Set(conversationIds));
      patchConversationsCache(uniqueIds_1, labelName_1, false);
      await Promise.allSettled(uniqueIds_1.map(conversationId_0 => deleteLabelMutation({
        data: {
          project_id: projectId,
          conversation_id: conversationId_0,
          name: labelName_1
        }
      })));
      invalidateAvailableLabelCaches(queryClient, projectId, "conversation");
      if (hasConversationLabelFilter) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
        });
      }
    };
    $[11] = conversationIds;
    $[12] = deleteLabelMutation;
    $[13] = hasConversationLabelFilter;
    $[14] = patchConversationsCache;
    $[15] = projectId;
    $[16] = queryClient;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const removeLabelFromMany = t3;
  const t4 = isAdding || isRemoving;
  let t5;
  if ($[18] !== addLabelToMany || $[19] !== removeLabelFromMany || $[20] !== t4) {
    t5 = {
      addLabelToMany,
      removeLabelFromMany,
      isAdding: t4
    };
    $[18] = addLabelToMany;
    $[19] = removeLabelFromMany;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}
function _temp6(l) {
  return l != null;
}
function _temp5(f) {
  return f.column === FILTERS.columnConversationLabel;
}